import {Injectable, OnDestroy} from '@angular/core';
import {BehaviorSubject, Observable, Subject, throwError} from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  first,
  map,
  retry,
  shareReplay,
  switchMap,
  takeUntil
} from 'rxjs/operators';
import {Store} from '@ngrx/store';

import {DbResponse} from '../shared/interceptors/db-response';
import {RequesterService} from '../shared/services/requester/requester.service';
import * as fromStore from '../store';
import {DbStoreState} from '../store';
import {GlobalEventDispatcherService} from '../shared/services/global-event-dispatcher/global-event-dispatcher.service';
import {Config} from '../config/config';
import {GlobalEvent} from '../shared/services/global-event-dispatcher/global-event';
import {clientRoles} from '../user/user-roles-actions';
import {Website} from '../websites/website/website';
import {UserActivity} from '../account/activity-log/user-activity.model';

import {deleteApp, FirebaseApp, initializeApp} from 'firebase/app';
import {Auth, getAuth, onAuthStateChanged, signInWithCustomToken} from 'firebase/auth';
import {
  Database,
  DatabaseReference,
  get,
  getDatabase,
  limitToLast,
  onChildAdded,
  onChildChanged,
  orderByChild,
  query,
  ref,
  startAt
} from 'firebase/database';
import {
  collection,
  DocumentData,
  Firestore,
  getDocs,
  getFirestore,
  onSnapshot,
  orderBy,
  query as fireStoreQuery,
  Timestamp,
  where
} from 'firebase/firestore';

interface ActivityNotification {
  id: string;
  progress?: number;
  status?: string;
  created_at: number;
  [key: string]: any;
}

interface WorkspaceIdEntities {
  ownerWorkspaceId: number;
  currentWorkspaceId: number;
  currentWorkspaceRoleId: number;
}

interface FirebaseConfig {
  app: FirebaseApp | null;
  auth: Auth | null;
  database: Database | null;
  firestore: Firestore | null;
}

interface FirebaseReferences {
  notifications?: DatabaseReference;
  activities?: DatabaseReference;
  domainActivities?: DatabaseReference;
  billingActivities?: DatabaseReference;
  workspaceActivities?: DatabaseReference;
}

interface SubscriptionState {
  initialized: boolean;
  references: DatabaseReference[];
  unsubscribers: (() => void)[];
}

const CACHE_TTL = 300000; // 5 minutes
const RETRY_ATTEMPTS = 3;
const DEBOUNCE_TIME = 100;

@Injectable()
export class ActivityNotificationService implements OnDestroy {
  private readonly destroy$ = new Subject<void>();
  private readonly activitySubject$ = new Subject<ActivityNotification>();
  private readonly notificationCache = new Map<string, { data: ActivityNotification; timestamp: number }>();

  private readonly authState$ = new BehaviorSubject<boolean>(false);
  private readonly workspaceState$ = new BehaviorSubject<WorkspaceIdEntities | null>(null);

  private firebase: FirebaseConfig = {
    app: null,
    auth: null,
    database: null,
    firestore: null
  };

  private firebaseReferences: FirebaseReferences = {};
  private subscriptionState: SubscriptionState = {
    initialized: false,
    references: [],
    unsubscribers: []
  };

  private isAuthenticating = false;
  private authenticationPromise: Promise<boolean> | null = null;

  constructor(
    private requester: RequesterService,
    private store: Store<DbStoreState>,
    private globalEventDispatcher: GlobalEventDispatcherService
  ) {
    this.initializeService();
  }

  ngOnDestroy(): void {
    this.cleanup();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private initializeService(): void {
    this.setupWorkspaceSubscription();
    this.setupEventListeners();
    this.initializeFirebase();
    // Initialize subscriptions when service starts
    this.authenticate();
  }

  private setupWorkspaceSubscription(): void {
    this.store.select(fromStore.getWorkspaceIdEntities)
      .pipe(
        filter(workspaceIdEntities => workspaceIdEntities?.currentWorkspaceId != null),
        distinctUntilChanged((a, b) =>
          a?.currentWorkspaceId === b?.currentWorkspaceId &&
          a?.currentWorkspaceRoleId === b?.currentWorkspaceRoleId
        ),
        debounceTime(DEBOUNCE_TIME),
        takeUntil(this.destroy$)
      )
      .subscribe((workspaceIdEntities: WorkspaceIdEntities) => {
        const normalizedEntities = {
          ownerWorkspaceId: workspaceIdEntities.ownerWorkspaceId,
          currentWorkspaceId: workspaceIdEntities.currentWorkspaceId ||
                               workspaceIdEntities.ownerWorkspaceId ||
                               Number(localStorage.getItem('dbCurrentWorkspaceId')),
          currentWorkspaceRoleId: workspaceIdEntities.currentWorkspaceRoleId ||
                                  Number(localStorage.getItem('dbCurrentWorkspaceRoleId'))
        };

        this.workspaceState$.next(normalizedEntities);
        this.handleWorkspaceChange();
      });
  }

  private setupEventListeners(): void {
    const eventHandlers = [
      { event: GlobalEvent.userLogIn, handler: () => this.authenticate() },
      { event: GlobalEvent.userLogOut, handler: () => this.signOut() },
      { event: GlobalEvent.workspaceSwitch, handler: () => this.handleWorkspaceSwitch() },
      { event: GlobalEvent.sessionRefreshed, handler: () => this.handleSessionRefresh() }
    ];

    eventHandlers.forEach(({ event, handler }) => {
      this.globalEventDispatcher.addEventListener(event, handler, this);
    });
  }

  private initializeFirebase(): void {
    try {
      this.cleanupFirebase();

      this.firebase.app = initializeApp(Config.firebaseConfig);
      this.firebase.auth = getAuth(this.firebase.app);
      this.firebase.database = getDatabase(this.firebase.app);
      this.firebase.firestore = getFirestore(this.firebase.app);
    } catch (error) {
      console.error('Firebase initialization failed:', error);
      throw new Error('Failed to initialize Firebase');
    }
  }

  private async handleWorkspaceChange(): Promise<void> {
    try {
      await this.resetSubscriptions();
      this.authState$.next(false);
      // Clear Firebase token since workspace changed
      localStorage.removeItem('dbFirebaseToken');
      await this.authenticate();

      // Dispatch NgRx actions to reload activities after workspace change
      this.store.dispatch(new fromStore.LoadActivities());
      this.store.dispatch(new fromStore.InitActivitiesSocket());
    } catch (error) {
      console.error('ActivityNotificationService: Workspace change failed:', error);
    }
  }

  private async handleWorkspaceSwitch(): Promise<void> {
    try {
      await this.signOut();
      this.initializeFirebase();
      // Add delay to ensure workspace switch is complete
      await new Promise(resolve => setTimeout(resolve, 1000));
      await this.authenticate();
    } catch (error) {
      console.error('Workspace switch failed:', error);
    }
  }

  private async handleSessionRefresh(): Promise<void> {
    try {
      await this.signOut();
      await this.authenticate();
    } catch (error) {
      console.error('Session refresh failed:', error);
    }
  }

  private async authenticate(): Promise<boolean> {
    if (this.isAuthenticating && this.authenticationPromise) {
      return this.authenticationPromise;
    }

    if (this.authState$.value) {
      return true;
    }

    this.isAuthenticating = true;
    this.authenticationPromise = this.performAuthentication();

    try {
      const result = await this.authenticationPromise;
      this.authState$.next(result);
      return result;
    } finally {
      this.isAuthenticating = false;
      this.authenticationPromise = null;
    }
  }

  private async performAuthentication(): Promise<boolean> {
    try {
      // Get fresh workspace data from store instead of cached BehaviorSubject
      const workspace = await new Promise<WorkspaceIdEntities>((resolve, reject) => {
        this.store.select(fromStore.getWorkspaceIdEntities).pipe(
          filter(ws => ws?.currentWorkspaceId != null),
          first()
        ).subscribe({
          next: resolve,
          error: reject
        });
      });

      if (!workspace?.currentWorkspaceId) {
        throw new Error('No workspace ID available');
      }

      // Update the local state with fresh workspace data
      this.workspaceState$.next(workspace);

      console.log('🔐 ActivityNotificationService: Starting Firebase auth for workspace:', workspace.currentWorkspaceId);
      await this.authenticateWithFirebase(workspace.currentWorkspaceId);
      console.log('✅ ActivityNotificationService: Firebase auth complete, setting up references...');
      await this.setupFirebaseReferences(workspace);
      console.log('✅ ActivityNotificationService: Firebase references setup complete');

      return true;
    } catch (error) {
      console.error('Authentication failed:', error);
      return false;
    }
  }

  private async authenticateWithFirebase(workspaceId: number): Promise<void> {
    const cachedToken = localStorage.getItem('dbFirebaseToken');

    try {
      if (cachedToken) {
        await signInWithCustomToken(this.firebase.auth!, cachedToken);
        return;
      }
    } catch (error) {
      localStorage.removeItem('dbFirebaseToken');
    }

    const token = await this.fetchFirebaseToken(workspaceId);
    localStorage.setItem('dbFirebaseToken', token);
    await signInWithCustomToken(this.firebase.auth!, token);
  }

  private async fetchFirebaseToken(workspaceId: number): Promise<string> {
    return new Promise((resolve, reject) => {
      this.requester.core
        .get(`workspaces/${workspaceId}/user/me/firebase`)
        .pipe(
          filter((response: DbResponse) => !!response),
          first(),
          retry(RETRY_ATTEMPTS),
          catchError(error => throwError(() => new Error(`Failed to fetch Firebase token: ${error.message}`)))
        )
        .subscribe({
          next: (response: DbResponse) => {
            if (response?.success && response.data?.token) {
              resolve(response.data.token);
            } else {
              reject(new Error('Invalid token response'));
            }
          },
          error: reject
        });
    });
  }

  private async setupFirebaseReferences(workspace?: WorkspaceIdEntities): Promise<void> {
    // Wait for Firebase auth to be ready
    if (!this.firebase.auth?.currentUser) {
      await new Promise<void>((resolve, reject) => {
        const timeout = setTimeout(() => {
          reject(new Error('Firebase auth timeout'));
        }, 5000);

        const unsubscribe = this.firebase.auth!.onAuthStateChanged((user) => {
          if (user) {
            clearTimeout(timeout);
            unsubscribe();
            resolve();
          }
        });
      });
    }

    const userId = this.firebase.auth?.currentUser?.uid;
    const workspaceData = workspace || this.workspaceState$.value;

    console.log('🔍 ActivityNotificationService: setupFirebaseReferences - UserId:', userId, 'Workspace:', workspaceData);

    if (!userId || !workspaceData) {
      throw new Error(`Missing authentication or workspace data. UserId: ${userId}, Workspace: ${JSON.stringify(workspaceData)}`);
    }

    this.firebaseReferences = {
      activities: ref(this.firebase.database!, `clients/${userId}/activities`)
    };

    if ([clientRoles.OWNER, clientRoles.COMPANY_ADMIN].includes(workspaceData.currentWorkspaceRoleId)) {
      this.firebaseReferences.billingActivities = ref(
        this.firebase.database!,
        `workspaces/${workspaceData.currentWorkspaceId}/billingActivities`
      );
    }

    if ([clientRoles.OWNER, clientRoles.COMPANY_ADMIN, clientRoles.COMPANY_DEV].includes(workspaceData.currentWorkspaceRoleId)) {
      this.firebaseReferences.workspaceActivities = ref(
        this.firebase.database!,
        `workspaces/${workspaceData.currentWorkspaceId}/activities/`
      );
    }

    await this.setupDomainActivities(workspaceData);

    // Force re-establishment of subscriptions after setting up references
    this.subscriptionState.initialized = false;
    this.setupActivitySubscriptions();

    // Also trigger domain subscriptions setup if we already have websites loaded
    if (this.subscriptionState.references.length > 0) {
      this.setupDomainActivitySubscriptions();
    }
  }

  private async setupDomainActivities(workspace?: WorkspaceIdEntities): Promise<void> {
    const workspaceData = workspace || this.workspaceState$.value;
    if (!workspaceData) {
      return;
    }

    this.store.select(fromStore.getWebsitesLoaded)
      .pipe(
        filter(Boolean),
        switchMap(() => this.store.select(fromStore.getAllWebsites)),
        distinctUntilChanged((prev, curr) => prev?.length === curr?.length),
        takeUntil(this.destroy$)
      )
      .subscribe((websites: Website[]) => {
        this.updateDomainReferences(websites, workspaceData);
      });
  }

  private updateDomainReferences(websites: Website[], workspace: WorkspaceIdEntities): void {
    const filteredWebsites = workspace.currentWorkspaceRoleId === clientRoles.OWNER
      ? websites
      : websites.filter(website => !!website.domain_hash);

    const newReferences = filteredWebsites
      .map(website => {
        const refPath = `workspaces/${workspace.currentWorkspaceId}/domainActivities/${website.id}`;
        return ref(this.firebase.database!, refPath);
      })
      .filter((newRef, index, refs) =>
        refs.findIndex(ref => ref.toString() === newRef.toString()) === index
      );

    // Check if references have actually changed to avoid duplicate subscriptions
    const existingRefs = this.subscriptionState.references.map(ref => ref.toString()).sort();
    const newRefs = newReferences.map(ref => ref.toString()).sort();
    const referencesChanged = JSON.stringify(existingRefs) !== JSON.stringify(newRefs);

    if (referencesChanged) {
      this.subscriptionState.references = newReferences;

      // Set up subscriptions for domain activities if we have any references
      if (newReferences.length > 0) {
        this.setupDomainActivitySubscriptions();
      }
    }
  }

  public getActivityNotifications(): Observable<ActivityNotification[]> {
    return new Observable(observer => {
      observer.next([]);
    });
  }

  public getActivities(): Observable<ActivityNotification[]> {
    return new Observable(observer => {
      this.authenticate().then(async () => {
        const activities: ActivityNotification[] = [];
        const queries = this.buildActivityQueries();

        try {
          const results = await Promise.allSettled(
            queries.map(queryRef => get(queryRef))
          );

          results.forEach(result => {
            if (result.status === 'fulfilled' && result.value.exists()) {
              const snapshots = this.processSnapshot(result.value);
              activities.push(...snapshots.reverse());
            }
          });

          observer.next(activities);
        } catch (error) {
          console.error('Failed to get activities:', error);
          observer.next([]);
        }
      }).catch(error => {
        console.error('Authentication failed:', error);
        observer.next([]);
      });
    });
  }

  private buildActivityQueries() {
    const queries: any[] = [];
    const timeThreshold = Date.now() - CACHE_TTL;

    const activityConfigs = [
      { ref: this.firebaseReferences.activities, limit: 2 },
      { ref: this.firebaseReferences.billingActivities, limit: 2 },
      { ref: this.firebaseReferences.workspaceActivities, limit: 2 }
    ];

    activityConfigs.forEach(({ ref, limit }) => {
      if (ref) {
        queries.push(
          query(
            ref,
            limitToLast(limit),
            orderByChild('created_at'),
            startAt(timeThreshold)
          )
        );
      }
    });

    this.subscriptionState.references.forEach(ref => {
      queries.push(
        query(
          ref,
          limitToLast(20),
          orderByChild('created_at'),
          startAt(timeThreshold)
        )
      );
    });

    return queries;
  }

  private processSnapshot(snapshot: any): ActivityNotification[] {
    const data = snapshot.val();
    return Object.entries(data || {}).map(([id, value]: [string, any]) => ({
      id,
      ...value
    }));
  }

  public subscribeToActivityNotifications(): Observable<ActivityNotification> {
    return new Observable<ActivityNotification>(() => {
      // No longer needed - notifications reference removed
    }).pipe(shareReplay(1));
  }

  public subscribeToActivities(useLocalCache = false): Observable<ActivityNotification> {
    const cache = useLocalCache ? new Map() : this.notificationCache;

    // Ensure subscriptions are set up when component subscribes
    this.authenticate();

    return this.activitySubject$.pipe(
      map(data => this.processCachedNotification(data, cache)),
      filter((data): data is ActivityNotification => data !== null)
    );
  }

  private processCachedNotification(
    data: ActivityNotification,
    cache: Map<string, any>
  ): ActivityNotification | null {
    const cacheKey = data.id;
    const cached = cache.get(cacheKey);
    const now = Date.now();

    if (!cached ||
        cached.timestamp < now - CACHE_TTL ||
        cached.data.progress !== data.progress ||
        cached.data.status !== data.status) {

      cache.set(cacheKey, { data: { ...data }, timestamp: now });
      return data;
    }

    return null;
  }

  private setupActivitySubscriptions(): void {
    if (this.subscriptionState.initialized) {
      return;
    }

    const subscriptionConfigs = [
      { ref: this.firebaseReferences.activities, limit: 2 },
      { ref: this.firebaseReferences.billingActivities, limit: 2 },
      { ref: this.firebaseReferences.workspaceActivities, limit: 2 }
    ];

    subscriptionConfigs.forEach(({ ref, limit }) => {
      if (ref) {
        this.createActivitySubscription(ref, limit);
      }
    });

    this.subscriptionState.initialized = true;
  }

  private setupDomainActivitySubscriptions(): void {
    this.subscriptionState.references.forEach((ref) => {
      this.createActivitySubscription(ref, 20); // Use limit of 20 for domain activities
    });
  }

  private createActivitySubscription(reference: DatabaseReference, limit: number): void {
    const timeThreshold = Date.now() - CACHE_TTL;
    const queryRef = query(
      reference,
      limitToLast(limit),
      orderByChild('created_at'),
      startAt(timeThreshold)
    );

    let isInitialized = false;

    const unsubscribers = [
      onChildAdded(queryRef, snapshot => {
        if (isInitialized) {
          const data = { id: snapshot.key!, ...snapshot.val() };
          this.activitySubject$.next(data);
        }
      }),
      onChildChanged(queryRef, snapshot => {
        const data = { id: snapshot.key!, ...snapshot.val() };
        this.activitySubject$.next(data);
      })
    ];

    get(queryRef)
      .then(() => {
        isInitialized = true;
      })
      .catch(error => {
        if (error.code !== 'PERMISSION_DENIED' && !error.message?.includes('Permission denied')) {
          console.error('ActivityNotificationService: Failed to initialize subscription:', error);
        }
      });

    this.subscriptionState.unsubscribers.push(...unsubscribers);
  }

  public async getUsersActivities(dateRange: [number, number] = [null, null]): Promise<DocumentData[]> {
    const workspace = this.workspaceState$.value;

    if (!this.firebase.firestore || !workspace) {
      throw new Error('Firebase not initialized or workspace not available');
    }

    if (![clientRoles.OWNER].includes(workspace.currentWorkspaceRoleId)) {
      throw new Error('Insufficient permissions');
    }

    const [startDate, endDate] = this.convertToTimestamp(dateRange);
    const activitiesCollection = collection(
      this.firebase.firestore,
      `workspaces/${workspace.currentWorkspaceId}/activities`
    );

    const q = fireStoreQuery(
      activitiesCollection,
      where('created_at', '>=', startDate),
      where('created_at', '<', endDate),
      orderBy('created_at', 'desc')
    );

    try {
      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error('Error getting users activities:', error);
      throw error;
    }
  }

  public getUsersActivitiesSnapshot(): Observable<UserActivity[]> {
    const workspace = this.workspaceState$.value;

    if (!this.firebase.firestore || !workspace) {
      return throwError(() => new Error('Firebase not initialized or workspace not available'));
    }

    if (![clientRoles.OWNER].includes(workspace.currentWorkspaceRoleId)) {
      return throwError(() => new Error('Insufficient permissions'));
    }

    return new Observable<UserActivity[]>(observer => {
      const activitiesCollection = collection(
        this.firebase.firestore!,
        `workspaces/${workspace.currentWorkspaceId}/activities`
      );
      const q = fireStoreQuery(activitiesCollection, orderBy('created_at', 'desc'));

      const unsubscribe = onSnapshot(q,
        snapshot => {
          const activities = snapshot.docChanges().map(change =>
            new UserActivity(change.doc.data())
          );
          observer.next(activities);
        },
        error => {
          console.error('Error in activities snapshot:', error);
          observer.error(error);
        }
      );

      this.subscriptionState.unsubscribers.push(unsubscribe);
      this.destroy$.subscribe(() => unsubscribe());
    }).pipe(shareReplay(1));
  }

  private convertToTimestamp(dateRange: [number, number]): [Timestamp, Timestamp] {
    let [startDate, endDate] = dateRange;

    if (!startDate) {
      const date = new Date();
      startDate = date.setMonth(date.getMonth() - 1);
    }

    if (!endDate) {
      endDate = Date.now();
    }

    return [
      Timestamp.fromDate(new Date(startDate)),
      Timestamp.fromDate(new Date(endDate))
    ];
  }

  public async getDeletedDomainsActivities(): Promise<DocumentData[]> {
    return this.getDeletedResourceActivities('deletedDomains');
  }

  public async getDeletedClientsActivities(): Promise<DocumentData[]> {
    return this.getDeletedResourceActivities('deletedClients');
  }

  private async getDeletedResourceActivities(resourceType: string): Promise<DocumentData[]> {
    const workspace = this.workspaceState$.value;

    if (!this.firebase.firestore || !workspace) {
      throw new Error('Firebase not initialized or workspace not available');
    }

    if (![clientRoles.OWNER].includes(workspace.currentWorkspaceRoleId)) {
      throw new Error('Insufficient permissions');
    }

    try {
      const resourceCollection = collection(
        this.firebase.firestore,
        `workspaces/${workspace.currentWorkspaceId}/${resourceType}`
      );
      const querySnapshot = await getDocs(resourceCollection);
      return querySnapshot.docs.map(doc => doc.data());
    } catch (error) {
      console.error(`Error getting ${resourceType} activities:`, error);
      throw error;
    }
  }

  private async signOut(): Promise<void> {
    await this.resetSubscriptions();

    if (this.firebase.auth) {
      await this.firebase.auth.signOut();
    }

    // Clear workspace state to ensure fresh data on next login
    this.workspaceState$.next(null);
    this.authState$.next(false);
    
    // Clear cached Firebase token
    localStorage.removeItem('dbFirebaseToken');
  }

  private async resetSubscriptions(): Promise<void> {
    this.subscriptionState.unsubscribers.forEach(unsubscribe => {
      try {
        unsubscribe();
      } catch (error) {
        console.error('Error unsubscribing:', error);
      }
    });

    this.subscriptionState = {
      initialized: false,
      references: [],
      unsubscribers: []
    };

    this.firebaseReferences = {};

    // Clear notification cache during workspace switch to ensure fresh notifications flow through
    this.notificationCache.clear();
  }

  private cleanupFirebase(): void {
    if (this.firebase.app) {
      deleteApp(this.firebase.app).catch(error => {
        console.error('Error cleaning up Firebase app:', error);
      });
    }

    this.firebase = {
      app: null,
      auth: null,
      database: null,
      firestore: null
    };
  }

  private cleanup(): void {
    this.resetSubscriptions();
    this.activitySubject$.complete();
    this.authState$.complete();
    this.workspaceState$.complete();
    this.notificationCache.clear();
    this.cleanupFirebase();
  }

  public sendActivityNotificationsReadMessage(): void {
    // Implementation placeholder - method kept for compatibility
  }
}
